'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

import { useAuth } from '~/context/AuthProvider'
import { useAuthUser } from '~/hooks/useAuthUser'
import {
  updateQueryStringParameter,
  getNavigationUriParam,
} from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'
import { TrackingProperties, sendTrackingData } from '~/utils/tracking'

interface ISplitsLink extends React.HTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode
  path?: string
  target?: 'tab' | 'window'
  className?: string
  chainId?: number
  underline?: boolean
  eventName?: string
  eventProperties?: TrackingProperties
  requireAuth?: boolean
  isDisabled?: boolean
}

export const SplitsLink = ({
  ref,
  path,
  children,
  target,
  className,
  chainId,
  underline = false,
  eventName,
  eventProperties,
  requireAuth,
  onClick,
  ...props
}: ISplitsLink & { ref?: React.RefObject<HTMLAnchorElement | null> }) => {
  const searchParams = useSearchParams()
  const chainIdParam = searchParams?.get('chainId')
  const { setLoginModalState } = useAuth()
  const { user: authUser } = useAuthUser()

  if (!path) {
    return <>{children}</>
  }

  let href = path
  if (path.includes('/accounts/') || path.includes('/new/')) {
    if (chainId) {
      href = updateQueryStringParameter({
        uri: path,
        key: 'chainId',
        value: chainId.toString(),
      })
    } else {
      href = getNavigationUriParam(chainIdParam, href)
    }
  }

  const opensExternally = target === 'tab' || target === 'window'

  return (
    <Link
      href={href}
      passHref={opensExternally}
      target={opensExternally ? '_blank' : undefined}
      ref={ref}
      rel={opensExternally ? 'noopener noreferrer' : undefined}
      onClick={(e) => {
        if (requireAuth && !authUser) {
          e.preventDefault()
          setLoginModalState(true)
        } else {
          if (target === 'window') {
            e.preventDefault()
            window.open(href, '_blank', 'noopener,noreferrer,popup')
          }
          if (eventName) sendTrackingData(eventName, eventProperties)
          if (onClick) onClick(e)
        }
      }}
      className={cn(className, {
        'underline underline-offset-2': underline,
      })}
      {...props}
    >
      {children}
    </Link>
  )
}

SplitsLink.displayName = 'SplitsLink'
